export interface AdsAttribution {
  party_id: number | null;
  oppref: string;
  obref: string;
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  campaign_id: string;
  ad_group_id: string;
  ad_id: string;
  utm_term: string;
  utm_content: string;
  click_id: string;
  landing_url: string;
}

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"] as const;
const AD_KEYS = ["campaign_id", "ad_group_id", "ad_id"] as const;
const TS_KEY = "employer_attr_ts";
const TTL_MS = 30 * 24 * 60 * 60 * 1000;

export function readCookie(name: string): string {
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const match = document.cookie.match(new RegExp("(?:^|; )" + escaped + "=([^;]*)"));
  return match ? decodeURIComponent(match[1]) : "";
}

function stored(key: string): string {
  try {
    return localStorage.getItem(key) || "";
  } catch (e) {
    return "";
  }
}

function setIfEmpty(key: string, value: string | null): void {
  if (!value) {
    return;
  }
  try {
    if (!localStorage.getItem(key)) {
      localStorage.setItem(key, value);
    }
  } catch (e) {
    // storage unavailable
  }
}

interface PortalAttribution {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  campaign_id?: string;
  ad_group_id?: string;
  ad_id?: string;
  party?: string;
  oppref?: string;
  click_id?: string;
  landing_url?: string;
}

function portalAttribution(): PortalAttribution {
  const raw = readCookie("drj_attr");
  if (!raw) {
    return {};
  }
  try {
    let json = raw;
    if (!raw.trim().startsWith("{")) {
      let b64 = raw.replace(/-/g, "+").replace(/_/g, "/");
      const pad = b64.length % 4;
      if (pad) {
        b64 += "=".repeat(4 - pad);
      }
      json = atob(b64);
    }
    const parsed = JSON.parse(json);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (e) {
    return {};
  }
}

export function captureAdsAttributionFromUrl(): void {
  try {
    const ts = parseInt(stored(TS_KEY), 10);
    if (!Number.isNaN(ts) && Date.now() - ts > TTL_MS) {
      clearAdsAttribution();
    }

    const params = new URLSearchParams(window.location.search);
    const party = params.get("Party");
    const oppref = params.get("oppref");
    const clickId = params.get("click_id") || params.get("clickid");

    setIfEmpty("employer_party", party);
    setIfEmpty("employer_oppref", oppref);
    setIfEmpty("employer_click_id", clickId);
    UTM_KEYS.forEach((key) => {
      setIfEmpty("employer_" + key, params.get(key));
    });
    AD_KEYS.forEach((key) => {
      setIfEmpty("employer_" + key, params.get(key));
    });

    const capturedSomething = Boolean(
      party || oppref || clickId || UTM_KEYS.some((key) => params.get(key)) || AD_KEYS.some((key) => params.get(key)),
    );
    if (capturedSomething && !stored("employer_landing_url")) {
      localStorage.setItem("employer_landing_url", window.location.href);
    }
    const hasStored = Boolean(
      stored("employer_party") ||
        stored("employer_oppref") ||
        stored("employer_click_id") ||
        UTM_KEYS.some((key) => stored("employer_" + key)) ||
        AD_KEYS.some((key) => stored("employer_" + key)),
    );
    if (hasStored && !stored(TS_KEY)) {
      localStorage.setItem(TS_KEY, String(Date.now()));
    }
  } catch (e) {
    // storage unavailable
  }
}

function pick(storedValue: string, portalValue: string | undefined, urlValue: string | null): string {
  return storedValue || portalValue || urlValue || "";
}

export function getAdsAttribution(): AdsAttribution {
  const params = new URLSearchParams(window.location.search);
  const portal = portalAttribution();
  const partyRaw = pick(stored("employer_party"), portal.party, params.get("Party"));
  const partyId = parseInt(partyRaw, 10);

  return {
    party_id: !Number.isNaN(partyId) && partyId > 0 ? partyId : null,
    oppref: pick(stored("employer_oppref"), portal.oppref, params.get("oppref")) || readCookie("__oppref") || readCookie("oppref"),
    obref: readCookie("__obref"),
    utm_source: pick(stored("employer_utm_source"), portal.utm_source, params.get("utm_source")),
    utm_medium: pick(stored("employer_utm_medium"), portal.utm_medium, params.get("utm_medium")),
    utm_campaign: pick(stored("employer_utm_campaign"), portal.utm_campaign, params.get("utm_campaign")),
    campaign_id: pick(stored("employer_campaign_id"), portal.campaign_id, params.get("campaign_id")),
    ad_group_id: pick(stored("employer_ad_group_id"), portal.ad_group_id, params.get("ad_group_id")),
    ad_id: pick(stored("employer_ad_id"), portal.ad_id, params.get("ad_id")),
    utm_term: pick(stored("employer_utm_term"), "", params.get("utm_term")),
    utm_content: pick(stored("employer_utm_content"), "", params.get("utm_content")),
    click_id: pick(stored("employer_click_id"), portal.click_id, params.get("click_id") || params.get("clickid")),
    landing_url: stored("employer_landing_url") || portal.landing_url || "",
  };
}

export function clearAdsAttribution(): void {
  try {
    localStorage.removeItem("employer_party");
    localStorage.removeItem("employer_oppref");
    localStorage.removeItem("employer_click_id");
    localStorage.removeItem("employer_landing_url");
    localStorage.removeItem(TS_KEY);
    UTM_KEYS.forEach((key) => localStorage.removeItem("employer_" + key));
    AD_KEYS.forEach((key) => localStorage.removeItem("employer_" + key));
  } catch (e) {
    // storage unavailable
  }
}
